import { zipSync } from 'fflate';
import { assemble, safeFileName, type PdfMetadata } from './pdfEngine';
import type { StampOptions } from './stamps';
import { bytesFor } from './documentStore';
import { downloadBlob } from './api';
import type { PageItem } from './types';

export interface OutputSpec {
  name: string;
  pages: PageItem[];
}

/**
 * Produces the finished file(s) and hands them to the browser.
 *
 * One output downloads as a PDF; several download as a ZIP. All of it happens
 * locally, so exporting works with no network.
 */
export async function exportOutputs(
  outputs: OutputSpec[],
  archiveName: string,
  metadata: PdfMetadata = {},
  stamps: StampOptions = {},
): Promise<void> {
  if (outputs.length === 1) {
    const spec = outputs[0]!;
    const bytes = await assemble(spec.pages, bytesFor, { ...metadata, title: metadata.title }, stamps);
    downloadBlob(bytes, safeFileName(spec.name), 'application/pdf');
    return;
  }

  // Build every PDF before zipping, so a failure part-way returns a clean error
  // rather than a truncated archive the browser has already saved.
  const files: Record<string, Uint8Array> = {};
  const used = new Set<string>();
  for (const spec of outputs) {
    // Each file in a split gets its own title, since they are separate
    // documents; the rest of the properties are shared.
    files[uniqueName(safeFileName(spec.name), used)] = await assemble(
      spec.pages,
      bytesFor,
      { ...metadata, title: metadata.title ? `${metadata.title} — ${spec.name}` : spec.name },
      // Each split file numbers itself from the start, which is what someone
      // splitting a bundle into separate documents expects.
      stamps,
    );
  }

  // PDFs are already compressed, so level 0 stores them: it is markedly faster
  // and the archive is essentially the same size.
  const zipped = zipSync(files, { level: 0 });
  downloadBlob(zipped, safeFileName(archiveName).replace(/\.pdf$/i, '.zip'), 'application/zip');
}

function uniqueName(name: string, used: Set<string>): string {
  if (!used.has(name)) {
    used.add(name);
    return name;
  }
  const base = name.replace(/\.pdf$/i, '');
  for (let n = 2; ; n += 1) {
    const candidate = `${base} (${n}).pdf`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
}
